import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Download, RefreshCw, FileCheck2, AlertOctagon, AlertTriangle, Check } from "lucide-react";

// Month-end compile & check: fix every blocking item, review warnings, then raise the remaining invoices.
const LABEL = {
  NOT_FINALISED: "Bill not finalised", INVOICE_MISMATCH: "Invoice differs from final bill", WEIGHT_MISMATCH: "Weight differs from Delhivery",
  DUPLICATE_INVOICE: "Duplicate invoices", CANCELLED_INVOICED: "Cancelled but invoiced", GST_ERROR: "GST error", NOTE_NEEDED: "Note / refund / collection open",
  NOT_INVOICED: "Delivered, not invoiced", CREDIT_STATUS: "Credit vs invoice status", CREDIT_LIMIT: "Credit limit", PAID_NO_REFERENCE: "Paid without reference",
  LOSS: "Below Delhivery cost", OVERDUE: "Overdue", PROOFS_PENDING: "Proofs to verify", TDS_CERT_PENDING: "TDS certificate pending",
};
const thisMonth = () => { const d = new Date(Date.now() + 330 * 60000); return d.toISOString().slice(0, 7); };

function Section({ title, rows, tone, isAdmin, onResolve }) {
  const groups = rows.reduce((m, r) => ((m[r.code] = m[r.code] || []).push(r), m), {});
  const [open, setOpen] = useState({});
  if (!rows.length) return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 inline-flex items-center gap-2"><Check className="w-4 h-4" />{title}: none</div>);
  return (
    <div className="space-y-2">
      <h2 className={`font-semibold inline-flex items-center gap-2 ${tone === "red" ? "text-red-700" : "text-amber-700"}`}>
        {tone === "red" ? <AlertOctagon className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{title} ({rows.length})</h2>
      {Object.entries(groups).map(([code, list]) => (
        <div key={code} className={`rounded-xl border ${tone === "red" ? "border-red-200" : "border-amber-200"} bg-white`}>
          <button onClick={() => setOpen((o) => ({ ...o, [code]: !o[code] }))} className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
            <span className="font-semibold text-slate-800">{LABEL[code] || code}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tone === "red" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{list.length}</span>
            <span className="ml-auto text-xs text-slate-400">{open[code] ? "hide" : "show"}</span>
          </button>
          {open[code] && (
            <ul className="divide-y divide-slate-100 border-t border-slate-100">{list.map((r, i) => (
              <li key={i} className="px-4 py-2 text-sm flex flex-wrap gap-x-3 gap-y-1 items-center">
                {r.order_id && <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5">{r.order_id}</span>}
                {r.invoice_number && <span className="font-mono text-xs text-slate-500">{r.invoice_number}</span>}
                {r.customer && <span className="text-slate-700">{r.customer}</span>}
                <span className="text-slate-600">{r.message}</span>
                {r.code === "NOTE_NEEDED" && isAdmin && r.adjustment_id && (
                  <button onClick={() => onResolve(r.adjustment_id)} className="ml-auto text-xs font-semibold text-emerald-700 hover:underline">Mark done</button>)}
              </li>))}</ul>)}
        </div>))}
    </div>
  );
}

export default function MonthEndCheck() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(thisMonth());
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try { const x = await api.get("/v2/month-end/check", { params: { month } }); setR(x.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month]);

  const csv = async () => {
    try {
      const x = await api.get("/v2/month-end/check.csv", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(x.data); const a = document.createElement("a");
      a.href = url; a.download = `Sendtoo_month_end_check_${month}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };
  const raise = async () => {
    if (!window.confirm(`Raise invoices for ${r.summary.raisable} delivered shipment(s) of ${month} that have no blocking issue?`)) return;
    setBusy(true);
    try {
      const x = await api.post("/v2/month-end/raise-remaining", null, { params: { month } });
      toast.success(`${x.data.raised.length} invoice(s) raised` + (x.data.failed.length ? `, ${x.data.failed.length} not raised` : ""));
      x.data.failed.slice(0, 5).forEach((f) => toast.error(String(f.reason)));
      await run();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not raise"); }
    finally { setBusy(false); }
  };
  const resolve = async (id) => {
    const note = window.prompt("What was done? (e.g. 'Credit note CN-0003 issued', 'Refunded Rs 50 cash')");
    if (!note || !note.trim()) return;
    try { await api.post(`/v2/month-end/adjustments/${id}/resolve`, { note: note.trim() }); toast.success("Marked done"); await run(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  return (
    <DashboardLayout title="Month-end check">
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5" data-testid="month-end-page">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Month-end check</h1>
            <p className="text-sm text-slate-500">Fix every blocking item, review the warnings, then raise the remaining invoices.</p>
          </div>
          <div className="ml-auto flex flex-wrap gap-2 items-center">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
            <button onClick={run} disabled={busy} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-300 text-sm font-semibold disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />Run again</button>
            <button onClick={csv} disabled={!r} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-300 text-sm font-semibold disabled:opacity-50"><Download className="w-4 h-4" />CSV</button>
          </div>
        </div>
        {!r ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (<>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Shipments booked", r.shipments, "text-slate-900"], ["Blocking", r.summary.blocking, r.summary.blocking ? "text-red-700" : "text-emerald-700"],
              ["Warnings", r.summary.warnings, "text-amber-700"], ["Ready to invoice", r.summary.raisable, "text-sky-700"]].map(([k, v, c]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">{k}</p><p className={`text-2xl font-bold ${c}`}>{v}</p></div>))}
          </div>
          {isAdmin && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex flex-wrap items-center gap-3">
              <FileCheck2 className="w-5 h-5 text-sky-700" />
              <p className="text-sm text-sky-900">{r.summary.raisable} delivered shipment(s) have no invoice and no blocking issue.{r.summary.blocking > 0 && " Shipments with blocking issues are skipped until fixed."}</p>
              <button onClick={raise} disabled={busy || !r.summary.raisable} data-testid="raise-remaining"
                className="ml-auto h-9 px-4 rounded-lg bg-sky-600 text-white text-sm font-semibold disabled:opacity-50">Raise remaining invoices</button>
            </div>)}
          <Section title="Blocking - fix before final invoicing" rows={r.blocking} tone="red" isAdmin={isAdmin} onResolve={resolve} />
          <Section title="Warnings - review" rows={r.warnings} tone="amber" isAdmin={isAdmin} onResolve={resolve} />
        </>)}
      </div>
    </DashboardLayout>
  );
}
